import { List, Map, Record } from '@gonfalon/immutable-shim/immutableShim';
import { isNotEmpty, optional, validateRecord } from '@gonfalon/legacy-form-validation/validationUtils';
import { genericCheckAccess } from '@gonfalon/permissions/access';
import type { ResourceActions } from '@gonfalon/permissions/actions';
import type { Access } from '@gonfalon/permissions/types';
import { isBefore } from 'date-fns';
import { isEmpty } from 'lodash';

import { createAccessDecision } from '#utils/access/records/AccessDecision';
import type { AccessChecks } from '#utils/access/types';
import type { Link } from '#utils/linkUtils';
import type { Member } from '#utils/members/records/Member';
import type { SessionConfig } from '#utils/sessionUtils';
import { createSessionConfig } from '#utils/sessionUtils';

import type { BillingContact } from './BillingContact';
import { createBillingContact } from './BillingContact';

export type AccountType = {
  _id: string;
  _links: Link;
  _access: AccessChecks | null;
  organization: string;
  sessionConfig: SessionConfig;
  requireMfa: boolean;
  billingContact: null | BillingContact;
  tokens: List<string>;
  enableAccountImpersonationUntil: Date | null;
  isSupportGenAiEnabled: boolean;
  isGeneralGenAiEnabled: boolean;
  canEnableDomainMatching: boolean;
  isDomainMatchingEnabled: boolean;
  isAutoSeatProvisioningEnabled: boolean;
};

export class Account extends Record<AccountType>({
  _id: '',
  _links: Map(),
  _access: null,
  organization: '',
  sessionConfig: createSessionConfig({}),
  requireMfa: false,
  billingContact: null,
  tokens: List(),
  enableAccountImpersonationUntil: null,
  isSupportGenAiEnabled: false,
  isGeneralGenAiEnabled: false,
  canEnableDomainMatching: false,
  isDomainMatchingEnabled: false,
  isAutoSeatProvisioningEnabled: false,
}) {
  selfLink() {
    return this._links.getIn(['self', 'href']);
  }

  validate() {
    return validateRecord(this, optional(isNotEmpty)('organization'));
  }

  getCanonicalBillingContact(owner: Member | undefined) {
    const contact = this.billingContact || createBillingContact();
    if (!owner) {
      return contact;
    }

    return contact.withMutations((c) => {
      if (isEmpty(c.name)) {
        c.set('name', owner.getFullName());
      }
      if (isEmpty(c.email)) {
        c.set('email', owner.email);
      }
    });
  }

  checkAccess({ profile }: { profile: Member }) {
    // must wrap in immutable accessDecision for compatibility
    // todo - switch to plain JS access decisions globally
    return (action?: string) => {
      const jsAccess = this._access ? (this._access.toJS() as Access) : undefined;
      const result = genericCheckAccess('acct', profile.toJS(), action as ResourceActions<'acct'>, jsAccess);
      return createAccessDecision(result);
    };
  }

  hasAddress() {
    const hasBillingAddress = !!(
      this.billingContact &&
      this.billingContact.address1 &&
      this.billingContact.city &&
      this.billingContact.postalCode &&
      this.billingContact.country
    );

    const hasTaxAddress = !!(
      this.billingContact &&
      this.billingContact.taxAddress &&
      this.billingContact.taxAddress.get('address1') &&
      this.billingContact.taxAddress.get('city') &&
      this.billingContact.taxAddress.get('postalCode') &&
      this.billingContact.taxAddress.get('country')
    );

    return hasBillingAddress || hasTaxAddress;
  }

  hasValidBillingContact() {
    return !!this.billingContact && this.hasAddress();
  }

  canEnableAutoSeatProvisioning() {
    return this.isDomainMatchingEnabled;
  }
}

export function createAccount(props = {}) {
  const account = props instanceof Account ? props : new Account(props);
  return account
    .update('billingContact', (bc) => (bc ? createBillingContact(bc) : null))
    .update('enableAccountImpersonationUntil', (d) => {
      if (!d) {
        return d;
      }
      return isBefore(d, Date.now()) ? null : d;
    });
}
